import React, { useState, useEffect } from 'react';
import './QuotationsManager.css';

const QuotationsManager = () => {
  const [quotations, setQuotations] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingQuotation, setEditingQuotation] = useState(null);
  const [formData, setFormData] = useState({
    id: '',
    quoteNumber: '',
    clientName: '',
    clientEmail: '',
    validUntil: '',
    items: [{ description: '', quantity: 1, price: 0 }],
    tax: 0,
    discount: 0,
    notes: '',
    status: 'draft'
  });

  useEffect(() => {
    loadQuotations();
  }, []);

  const loadQuotations = () => {
    const saved = localStorage.getItem('quotations');
    if (saved) {
      setQuotations(JSON.parse(saved));
    }
  };

  const saveQuotations = (quotationsData) => {
    localStorage.setItem('quotations', JSON.stringify(quotationsData));
    setQuotations(quotationsData);
  };

  const generateQuoteNumber = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    const random = Math.floor(Math.random() * 1000).toString().padStart(3, '0');
    return `QUO-${year}${month}${day}-${random}`;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const newQuotations = [...quotations];
    
    if (editingQuotation) {
      const index = newQuotations.findIndex(q => q.id === editingQuotation.id);
      newQuotations[index] = { ...formData };
    } else {
      const newQuotation = {
        ...formData,
        id: Date.now().toString(),
        quoteNumber: generateQuoteNumber(),
        createdAt: new Date().toISOString()
      };
      newQuotations.push(newQuotation);
    }
    
    saveQuotations(newQuotations);
    resetForm();
    setShowModal(false);
  };

  const resetForm = () => {
    setFormData({
      id: '',
      quoteNumber: '',
      clientName: '',
      clientEmail: '',
      validUntil: '',
      items: [{ description: '', quantity: 1, price: 0 }],
      tax: 0,
      discount: 0,
      notes: '',
      status: 'draft'
    });
    setEditingQuotation(null);
  };

  const handleEdit = (quotation) => {
    setFormData(quotation);
    setEditingQuotation(quotation);
    setShowModal(true);
  };

  const handleDelete = (id) => {
    if (window.confirm('Are you sure you want to delete this quotation?')) {
      const newQuotations = quotations.filter(q => q.id !== id);
      saveQuotations(newQuotations);
    }
  };

  const addItem = () => {
    setFormData({
      ...formData,
      items: [...formData.items, { description: '', quantity: 1, price: 0 }]
    });
  };

  const removeItem = (index) => {
    const newItems = formData.items.filter((_, i) => i !== index);
    setFormData({ ...formData, items: newItems });
  };

  const updateItem = (index, field, value) => {
    const newItems = [...formData.items];
    newItems[index][field] = value;
    setFormData({ ...formData, items: newItems });
  };

  const calculateTotal = () => {
    const subtotal = formData.items.reduce((sum, item) => sum + (item.quantity * item.price), 0);
    const taxAmount = subtotal * (formData.tax / 100);
    const discountAmount = subtotal * (formData.discount / 100);
    return subtotal + taxAmount - discountAmount;
  };

  return (
    <div className="quotations-manager">
      <div className="quotations-header">
        <h2>Quotations Management</h2>
        <button onClick={() => setShowModal(true)} className="add-quotation-btn">
          + Create New Quotation
        </button>
      </div>

      <div className="quotations-grid">
        {quotations.map(quotation => (
          <div key={quotation.id} className="quotation-card">
            <div className="quotation-header">
              <h3>{quotation.quoteNumber}</h3>
              <span className={`status ${quotation.status}`}>{quotation.status}</span>
            </div>
            <div className="quotation-details">
              <p><strong>Client:</strong> {quotation.clientName}</p>
              <p><strong>Valid Until:</strong> {quotation.validUntil}</p>
              <p><strong>Total:</strong> ${calculateQuotationTotal(quotation).toFixed(2)}</p>
            </div>
            <div className="quotation-actions">
              <button onClick={() => handleEdit(quotation)} className="edit-btn">Edit</button>
              <button onClick={() => handleDelete(quotation.id)} className="delete-btn">Delete</button>
            </div>
          </div>
        ))}
      </div>

      {showModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>{editingQuotation ? 'Edit Quotation' : 'Create New Quotation'}</h3>
              <button onClick={() => { setShowModal(false); resetForm(); }} className="close-btn">×</button>
            </div>
            
            <form onSubmit={handleSubmit} className="quotation-form">
              <div className="form-row">
                <div className="form-group">
                  <label>Client Name</label>
                  <input
                    type="text"
                    value={formData.clientName}
                    onChange={(e) => setFormData({ ...formData, clientName: e.target.value })}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Client Email</label>
                  <input
                    type="email"
                    value={formData.clientEmail}
                    onChange={(e) => setFormData({ ...formData, clientEmail: e.target.value })}
                    required
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Valid Until</label>
                  <input
                    type="date"
                    value={formData.validUntil}
                    onChange={(e) => setFormData({ ...formData, validUntil: e.target.value })}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Status</label>
                  <select
                    value={formData.status}
                    onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                  >
                    <option value="draft">Draft</option>
                    <option value="sent">Sent</option>
                    <option value="accepted">Accepted</option>
                    <option value="rejected">Rejected</option>
                    <option value="expired">Expired</option>
                  </select>
                </div>
              </div>

              <div className="items-section">
                <h4>Items</h4>
                {formData.items.map((item, index) => (
                  <div key={index} className="item-row">
                    <input
                      type="text"
                      placeholder="Description"
                      value={item.description}
                      onChange={(e) => updateItem(index, 'description', e.target.value)}
                      required
                    />
                    <input
                      type="number"
                      placeholder="Qty"
                      value={item.quantity}
                      onChange={(e) => updateItem(index, 'quantity', parseInt(e.target.value))}
                      min="1"
                    />
                    <input
                      type="number"
                      placeholder="Price"
                      value={item.price}
                      onChange={(e) => updateItem(index, 'price', parseFloat(e.target.value))}
                      min="0"
                      step="0.01"
                    />
                    <button
                      type="button"
                      onClick={() => removeItem(index)}
                      className="remove-item-btn"
                    >
                      Remove
                    </button>
                  </div>
                ))}
                <button type="button" onClick={addItem} className="add-item-btn">
                  + Add Item
                </button>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Tax (%)</label>
                  <input
                    type="number"
                    value={formData.tax}
                    onChange={(e) => setFormData({ ...formData, tax: parseFloat(e.target.value) })}
                    min="0"
                    step="0.01"
                  />
                </div>
                <div className="form-group">
                  <label>Discount (%)</label>
                  <input
                    type="number"
                    value={formData.discount}
                    onChange={(e) => setFormData({ ...formData, discount: parseFloat(e.target.value) })}
                    min="0"
                    step="0.01"
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Notes</label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  rows="3"
                />
              </div>

              <div className="total-section">
                <h3>Total: ${calculateTotal().toFixed(2)}</h3>
              </div>

              <div className="form-actions">
                <button type="button" onClick={() => { setShowModal(false); resetForm(); }} className="cancel-btn">
                  Cancel
                </button>
                <button type="submit" className="save-btn">
                  {editingQuotation ? 'Update Quotation' : 'Create Quotation'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

const calculateQuotationTotal = (quotation) => {
  const subtotal = quotation.items.reduce((sum, item) => sum + (item.quantity * item.price), 0);
  const taxAmount = subtotal * (quotation.tax / 100);
  const discountAmount = subtotal * (quotation.discount / 100);
  return subtotal + taxAmount - discountAmount;
};

export default QuotationsManager; 